import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

// Festival Theme Types
export type FestivalType = 
  | 'none' 
  | 'christmas' 
  | 'new_year' 
  | 'valentines' 
  | 'easter' 
  | 'diwali' 
  | 'halloween' 
  | 'thanksgiving'
  | 'independence_day'
  | 'mothers_day'
  | 'fathers_day';

export interface FestivalTheme {
  id: FestivalType;
  name: string;
  emoji: string;
  description: string;
  // Colors
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  gradientFrom: string;
  gradientTo: string;
  gradientVia?: string;
  // Decorative elements
  decorativeEmojis: string[];
  particleType: 'snow' | 'confetti' | 'hearts' | 'stars' | 'leaves' | 'fireworks' | 'none';
  // Hero section customization
  heroOverlayOpacity: number;
  heroBadgeText: string;
  heroBadgeEmoji: string;
  // Card decorations
  cardBorderColor: string;
  cardGlow: string;
}

// Festival Theme Configurations
export const festivalThemes: Record<FestivalType, FestivalTheme> = {
  none: {
    id: 'none',
    name: 'Default',
    emoji: '✨',
    description: 'No special theme',
    primaryColor: '#06b6d4',
    secondaryColor: '#3b82f6',
    accentColor: '#8b5cf6',
    gradientFrom: '#06b6d4',
    gradientTo: '#3b82f6',
    decorativeEmojis: [],
    particleType: 'none',
    heroOverlayOpacity: 0,
    heroBadgeText: '',
    heroBadgeEmoji: '',
    cardBorderColor: 'border-gray-200',
    cardGlow: '',
  },
  christmas: {
    id: 'christmas',
    name: 'Christmas',
    emoji: '🎄',
    description: 'Festive Christmas theme with snow',
    primaryColor: '#dc2626',
    secondaryColor: '#16a34a',
    accentColor: '#fbbf24',
    gradientFrom: '#dc2626',
    gradientTo: '#16a34a',
    gradientVia: '#15803d',
    decorativeEmojis: ['🎄', '🎅', '🎁', '⭐', '🦌', '❄️', '🔔', '🕯️'],
    particleType: 'snow',
    heroOverlayOpacity: 0.1,
    heroBadgeText: 'Merry Christmas!',
    heroBadgeEmoji: '🎄',
    cardBorderColor: 'border-red-200',
    cardGlow: 'shadow-red-100',
  },
  new_year: {
    id: 'new_year',
    name: 'New Year',
    emoji: '🎆',
    description: 'Celebrate the New Year!',
    primaryColor: '#fbbf24',
    secondaryColor: '#7c3aed',
    accentColor: '#ec4899',
    gradientFrom: '#7c3aed',
    gradientTo: '#fbbf24',
    gradientVia: '#ec4899',
    decorativeEmojis: ['🎆', '🎇', '🥳', '🍾', '🎊', '✨', '🎉', '🥂'],
    particleType: 'fireworks',
    heroOverlayOpacity: 0.15,
    heroBadgeText: 'Happy New Year 2026!',
    heroBadgeEmoji: '🎆',
    cardBorderColor: 'border-purple-200',
    cardGlow: 'shadow-purple-100',
  },
  valentines: {
    id: 'valentines',
    name: "Valentine's Day",
    emoji: '💝',
    description: 'Love is in the air',
    primaryColor: '#ec4899',
    secondaryColor: '#f43f5e',
    accentColor: '#fda4af',
    gradientFrom: '#ec4899',
    gradientTo: '#f43f5e',
    decorativeEmojis: ['💝', '💕', '❤️', '💖', '🌹', '💘', '💗', '😍'],
    particleType: 'hearts',
    heroOverlayOpacity: 0.1,
    heroBadgeText: "Happy Valentine's Day!",
    heroBadgeEmoji: '💝',
    cardBorderColor: 'border-pink-200',
    cardGlow: 'shadow-pink-100',
  },
  easter: {
    id: 'easter',
    name: 'Easter',
    emoji: '🐰',
    description: 'Spring has sprung!',
    primaryColor: '#a78bfa',
    secondaryColor: '#fbbf24',
    accentColor: '#34d399',
    gradientFrom: '#a78bfa',
    gradientTo: '#fbbf24',
    gradientVia: '#f0abfc',
    decorativeEmojis: ['🐰', '🥚', '🐣', '🌷', '🌸', '🦋', '🐥', '🌼'],
    particleType: 'confetti',
    heroOverlayOpacity: 0.08,
    heroBadgeText: 'Happy Easter!',
    heroBadgeEmoji: '🐰',
    cardBorderColor: 'border-violet-200',
    cardGlow: 'shadow-violet-100',
  },
  diwali: {
    id: 'diwali',
    name: 'Diwali',
    emoji: '🪔',
    description: 'Festival of Lights',
    primaryColor: '#f59e0b',
    secondaryColor: '#dc2626',
    accentColor: '#fbbf24',
    gradientFrom: '#f59e0b',
    gradientTo: '#dc2626',
    gradientVia: '#ea580c',
    decorativeEmojis: ['🪔', '✨', '🎆', '🎇', '🕯️', '💫', '🌟', '🏮'],
    particleType: 'fireworks',
    heroOverlayOpacity: 0.12,
    heroBadgeText: 'Happy Diwali!',
    heroBadgeEmoji: '🪔',
    cardBorderColor: 'border-orange-200',
    cardGlow: 'shadow-orange-100',
  },
  halloween: {
    id: 'halloween',
    name: 'Halloween',
    emoji: '🎃',
    description: 'Spooky season!',
    primaryColor: '#f97316',
    secondaryColor: '#7c3aed',
    accentColor: '#22c55e',
    gradientFrom: '#f97316',
    gradientTo: '#7c3aed',
    gradientVia: '#171717',
    decorativeEmojis: ['🎃', '👻', '🦇', '🕷️', '🕸️', '💀', '🧙', '🌙'],
    particleType: 'confetti',
    heroOverlayOpacity: 0.2,
    heroBadgeText: 'Happy Halloween!',
    heroBadgeEmoji: '🎃',
    cardBorderColor: 'border-orange-300',
    cardGlow: 'shadow-orange-200',
  },
  thanksgiving: {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    emoji: '🦃',
    description: 'Give thanks!',
    primaryColor: '#ea580c',
    secondaryColor: '#b45309',
    accentColor: '#dc2626',
    gradientFrom: '#ea580c',
    gradientTo: '#b45309',
    decorativeEmojis: ['🦃', '🍂', '🍁', '🌽', '🎃', '🥧', '🌾', '🍎'],
    particleType: 'leaves',
    heroOverlayOpacity: 0.1,
    heroBadgeText: 'Happy Thanksgiving!',
    heroBadgeEmoji: '🦃',
    cardBorderColor: 'border-amber-200',
    cardGlow: 'shadow-amber-100',
  },
  independence_day: {
    id: 'independence_day',
    name: 'Independence Day',
    emoji: '🇺🇸',
    description: 'Celebrate freedom!',
    primaryColor: '#dc2626',
    secondaryColor: '#1d4ed8',
    accentColor: '#ffffff',
    gradientFrom: '#dc2626',
    gradientTo: '#1d4ed8',
    decorativeEmojis: ['🇺🇸', '🎆', '🎇', '⭐', '🦅', '🗽', '🎉', '✨'],
    particleType: 'fireworks',
    heroOverlayOpacity: 0.1,
    heroBadgeText: 'Happy 4th of July!',
    heroBadgeEmoji: '🇺🇸',
    cardBorderColor: 'border-blue-200',
    cardGlow: 'shadow-blue-100',
  },
  mothers_day: {
    id: 'mothers_day',
    name: "Mother's Day",
    emoji: '💐',
    description: 'Celebrate moms!',
    primaryColor: '#ec4899',
    secondaryColor: '#f472b6',
    accentColor: '#c084fc',
    gradientFrom: '#ec4899',
    gradientTo: '#f472b6',
    decorativeEmojis: ['💐', '🌸', '💕', '👩‍👧', '🌷', '💝', '🌺', '❤️'],
    particleType: 'hearts',
    heroOverlayOpacity: 0.08,
    heroBadgeText: "Happy Mother's Day!",
    heroBadgeEmoji: '💐',
    cardBorderColor: 'border-pink-200',
    cardGlow: 'shadow-pink-100',
  },
  fathers_day: {
    id: 'fathers_day',
    name: "Father's Day",
    emoji: '👔',
    description: 'Celebrate dads!',
    primaryColor: '#1d4ed8',
    secondaryColor: '#0ea5e9',
    accentColor: '#fbbf24',
    gradientFrom: '#1d4ed8',
    gradientTo: '#0ea5e9',
    decorativeEmojis: ['👔', '🎩', '👨‍👧', '🏆', '⭐', '💙', '🎁', '👨‍👦'],
    particleType: 'confetti',
    heroOverlayOpacity: 0.08,
    heroBadgeText: "Happy Father's Day!",
    heroBadgeEmoji: '👔',
    cardBorderColor: 'border-blue-200',
    cardGlow: 'shadow-blue-100',
  },
};

// Default theme value (used when provider is not present)
const defaultThemeValue: FestivalThemeContextType = {
  currentTheme: festivalThemes['none'],
  currentThemeId: 'none',
  setTheme: () => {},
  isLoading: false,
};

interface FestivalThemeContextType {
  currentTheme: FestivalTheme;
  currentThemeId: FestivalType;
  setTheme: (themeId: FestivalType) => void;
  isLoading: boolean;
}

// Create context with default value (so it works even without provider)
const FestivalThemeContext = createContext<FestivalThemeContextType>(defaultThemeValue);

export function FestivalThemeProvider({ children }: { children: ReactNode }) {
  const [currentThemeId, setCurrentThemeId] = useState<FestivalType>('none');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'festival_theme')
        .maybeSingle();

      if (data && !error) {
        const themeValue = data.value as FestivalType;
        // Validate that the theme exists
        if (festivalThemes[themeValue]) {
          setCurrentThemeId(themeValue);
        }
      }
    } catch (error) {
      console.error('Error loading festival theme:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setTheme = async (themeId: FestivalType) => {
    try {
      // First, try to update existing record
      const { data: existing } = await supabase
        .from('app_settings')
        .select('id')
        .eq('key', 'festival_theme')
        .maybeSingle();

      let error;
      
      if (existing) {
        // Update existing
        const result = await supabase
          .from('app_settings')
          .update({ 
            value: themeId,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'festival_theme');
        error = result.error;
      } else {
        // Insert new
        const result = await supabase
          .from('app_settings')
          .insert({ 
            key: 'festival_theme', 
            value: themeId 
          });
        error = result.error;
      }

      if (error) {
        console.error('Error saving festival theme:', error);
        alert(`Failed to save theme: ${error.message}\n\nMake sure you have run the SQL migration and you are an admin.`);
      } else {
        setCurrentThemeId(themeId);
      }
    } catch (error) {
      console.error('Error saving festival theme:', error);
      alert('Failed to save theme. Check console for details.');
    }
  };

  return (
    <FestivalThemeContext.Provider
      value={{
        currentTheme: festivalThemes[currentThemeId],
        currentThemeId,
        setTheme,
        isLoading,
      }}
    >
      {children}
    </FestivalThemeContext.Provider>
  );
}

// Hook that returns default values if provider is not present (no error thrown)
export function useFestivalTheme(): FestivalThemeContextType {
  const context = useContext(FestivalThemeContext);
  // Context will always have a value due to defaultValue, so this is safe
  return context;
}

// Optional: Hook that throws if provider is required
export function useFestivalThemeStrict(): FestivalThemeContextType {
  const context = useContext(FestivalThemeContext);
  // Check if we're using the actual provider by checking if setTheme is a real function
  if (context.setTheme === defaultThemeValue.setTheme) {
    throw new Error('useFestivalThemeStrict must be used within a FestivalThemeProvider');
  }
  return context;
}
